import {AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, Injector, OnInit, ViewChild} from '@angular/core';
import {
    AddAssistanceApplicationStateAuditViewModel,
    AssistanceApplication,
    AssistanceApplicationClient,
    AssistanceApplicationReviewClient,
    AssistanceApplicationStateAuditType,
    AssistanceApplicationType,
    AuditType,
    Client,
    PledgeStatusType,
    ResubmitAssistanceApplicationRequest,
    User,
    UserType
} from '../../../../../../services/api.client';
import {BehaviorSubject, EMPTY, forkJoin, Observable, of} from 'rxjs';
import {FormArray, FormBuilder} from '@angular/forms';
import {ClientService} from '../../../../../../services/ClientService';
import {catchError, switchMap, takeUntil} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {AssistanceApplicationFormComponent} from '../assistance-application-form/assistance-application-form.component';
import {AssistanceApplicationStateAuditService} from '../../../../../../services/assistanceApplicationStateAudit.service';
import {PromptComponent} from '../../../../../shared/dialogs/PromptComponent';
import {Disposable} from '../../../../../../utilities/Disposable';
import {SecurityService} from '../../../../../../services/SecurityService';
import {toBoolean} from '../../../../../../utilities/Util';
import {NotificationService} from '../../../../../../services/NotificationService';
import {CompanyCategoryService} from '../../../../../../services/companyCategory.service';
import {AssistanceApplicationStatus} from '../../../../../../shared/enums/assistance-application-status';
import {Validators} from '../../../../../../utilities/Validators';
import swal from 'sweetalert2';
import {AssistanceApplicationAuditType} from '../../../../../../shared/enums/assistance-application-audit-type';
import {FundAdministrativeAgencyNameMap} from '../../../../../../shared/constants/FundConstants';
import {AnnualIncomeService} from "../../../../../../services/AnnualIncomeService";
import {AssistanceApplicationTypeStrings} from "../../../../../../shared/constants/application-type-strings";

@Component({
    selector: 'app-edit-assistance-application',
    templateUrl: './edit-assistance-application.component.html',
    styleUrls: ['./edit-assistance-application.component.scss'],
    standalone: false
})
export class EditAssistanceApplicationComponent extends Disposable implements OnInit, AfterViewInit, AfterContentChecked {

    constructor(private assistanceApplicationClient: AssistanceApplicationClient,
                private activatedRoute: ActivatedRoute,
                private injector: Injector,
                private cdref: ChangeDetectorRef,
                private formBuilder: FormBuilder,
                private _clientService: ClientService,
                private _assistanceApplicationStateAuditService: AssistanceApplicationStateAuditService,
                private _assistanceApplicationCategoryThresholdService: CompanyCategoryService,
                private _infoRequestClient: AssistanceApplicationReviewClient,
                private annualIncomeService: AnnualIncomeService,
                private _securityService: SecurityService,
                private _notificationService: NotificationService,) {
        super();
        this.applicationId = this.activatedRoute.snapshot.paramMap.get('assistanceApplicationId');

        this.client$ = this._clientService.currentClient$;
        this.user$ = this._securityService.currentUser$;
    }

    get canEditBasedOnAudit(): boolean {
        const auditType = this.assistanceApplication?.fundType?.auditType;
        const isNoneAudit = auditType.toString() === AssistanceApplicationAuditType.None;
        const isPendingReAudit = this.auditState === AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.PendingReAudit];
        return isNoneAudit || isPendingReAudit ||
            (this.assistanceApplication?.status !== AssistanceApplicationStatus.Approved
                && this.assistanceApplication?.status !== AssistanceApplicationStatus.UnderReview
                && this.assistanceApplication?.status !== AssistanceApplicationStatus.ReviewPassed
            && this.assistanceApplication?.status !== AssistanceApplicationStatus.UnderAgencyReview);
    }

    get canResubmit(): boolean {
        return this.assistanceApplication?.status === AssistanceApplicationStatus.Denied
            && (this.user?.userType === UserType.SystemAdmin
                || this.user?.userType === UserType.RegionAdmin
                || this.user?.userType === UserType.AgencyAdmin
                || this.isApplicationCreator);
    }

    get canUploadDocuments(): boolean {
        return this.assistanceApplication?.status === AssistanceApplicationStatus.UnderReview
            || this.assistanceApplication?.status === AssistanceApplicationStatus.UnderAgencyReview
            || this.assistanceApplication?.status === AssistanceApplicationStatus.Denied
            || this.assistanceApplication?.status === AssistanceApplicationStatus.InfoNeeded;
    }

    get isApplicationCreator(): boolean {
        return toBoolean(this.assistanceApplication && this.assistanceApplication.createdByUserId === this.user?.id);
    }

    get isUnderReview(): boolean {
        return (this.assistanceApplication.status !== AssistanceApplicationStatus.UnderReview
            && this.assistanceApplication.status !== AssistanceApplicationStatus.UnderAgencyReview);
    }

    get applicationPledgeStatusAllowsEdit(): boolean {
        if (!this.assistanceApplication.currentPledgeStatus) {
            return true;
        }

        return this.assistanceApplication?.currentPledgeStatus !== PledgeStatusType.Pending &&
            this.assistanceApplication?.currentPledgeStatus !== PledgeStatusType.PendingManual &&
            this.assistanceApplication?.currentPledgeStatus !== PledgeStatusType.Paid;
    }

    public get hasTranslation(): boolean {
        return this.assistanceApplication?.hasTranslation;
    }
    client: Client;
    client$: Observable<Client>;

    @ViewChild(AssistanceApplicationFormComponent) assistanceApplicationForm: AssistanceApplicationFormComponent;
    @ViewChild('auditModal') auditModal: PromptComponent;

    assistanceApplication: AssistanceApplication;
    assistanceApplicationSubject = new BehaviorSubject<AssistanceApplication>(null);
    applicationId: string;
    public administrativeFundName: string;

    user: User;
    user$: Observable<User>;
    loading = true;
    showSaveButton = true;
    auditState: string;
    formValuesSet = false;

    hasLoadingError = false;

    savedAudit: AddAssistanceApplicationStateAuditViewModel;

    protected readonly AuditType = AuditType;
    protected readonly AssistanceApplicationStatus = AssistanceApplicationStatus;

    public hasMaxIncomeLoadError = false;

    /**
     * Helper to map string or number applicationType to enum value
     */
    private getApplicationTypeEnumValue(type: string | number): AssistanceApplicationType | undefined {
        // applicationType is now the string enum value; the numeric branch is a
        // legacy fallback that no longer occurs at runtime (cast via unknown to
        // satisfy the string-enum type).
        if (typeof type === 'number') { return type as unknown as AssistanceApplicationType; }
        return AssistanceApplicationType[type as keyof typeof AssistanceApplicationType];
    }


    ngOnInit(): void {
        this.loading = true;
        this.user$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(user => this.user = user);
        this.client$
            .pipe(takeUntil(this.$destroyed))
            .subscribe(client => this.client = client);
    }

    ngAfterViewInit(): void {
        forkJoin([
            this.assistanceApplicationClient.getById(this.applicationId),
            this._assistanceApplicationStateAuditService.retrieveCaseCurrentState(this.applicationId)
        ]).pipe(
            catchError(error => {
                return EMPTY;
            }),
            switchMap(([application, auditState]) => {
                this.assistanceApplication = application;
                this.auditState = auditState?.newState
                    ? auditState?.newState?.toString()
                    : auditState?.oldState?.toString() || AssistanceApplicationStateAuditType.NotYetAudited.toString();
                // tslint:disable-next-line:max-line-length
                return this._assistanceApplicationCategoryThresholdService.getCategoryThresholds(this.assistanceApplication.applicationType);
            })
        ).subscribe({
            next: (categoryThresholds) => {
                this.assistanceApplicationSubject.next(this.assistanceApplication);
                this.administrativeFundName = FundAdministrativeAgencyNameMap[
                    this.getApplicationTypeEnumValue(this.assistanceApplication.applicationType)];
                switch (this.assistanceApplication?.applicationType?.toString()) {
                    case AssistanceApplicationTypeStrings.UFF:
                        this.hasMaxIncomeLoadError = this.annualIncomeService.hasUffIncomeLevelError;
                        break;
                    case AssistanceApplicationTypeStrings.UHONS:
                        this.hasMaxIncomeLoadError = this.annualIncomeService.hasUhonsIncomeLevelError;
                        break;
                    case AssistanceApplicationTypeStrings.STLWater:
                        this.hasMaxIncomeLoadError = this.annualIncomeService.hasStlWaterIncomeLevelError;
                        break;
                    default:
                        this.hasMaxIncomeLoadError = false;
                }
                if (this.hasMaxIncomeLoadError) {
                    this._notificationService.showError('An error occurred while fetching the max annual income levels. This could affect the household income validation throughout the application. If this issue persists, please contact an administrator for support.',
                        30,
                        true);
                }
                this.loading = false;
                setTimeout(() => {
                    this.setupChild();
                }, 500);
                this._assistanceApplicationCategoryThresholdService.setCategoryThresholds(categoryThresholds);
            }, error: () => {
                this.loading = false;
                this.hasLoadingError = true;
                this._notificationService.showError('An error occurred while retrieving the application or it was not found.');
            }
        });
    }

    setupChild(): void {

        this.assistanceApplicationForm?.form?.patchValue({
            ...this.assistanceApplication,
        });

        this.assistanceApplicationForm.companiesFormArray?.clear();

        this.assistanceApplication.companies.forEach(p => {
            this.assistanceApplicationForm.companyFormArrayMetaData.push({ignoreDuplicates: false, duplicateCompany: null});
            this.assistanceApplicationForm.companiesFormArray.push(this.assistanceApplicationForm.loadPayeeForm(p));
        });

        this.populateHouseHoldInformation();
        this.populateFiles();

        this.assistanceApplicationForm.form.get('translatedDescription').disable();

        if (this.assistanceApplication.hasTranslation) {
            this.assistanceApplicationForm.form.get('description').disable();
        }

        if (!this.canEdit()) {
            this.assistanceApplicationForm.form.disable();
            this.showSaveButton = false;
        }

        if (this.canUploadDocuments) {
            this.assistanceApplicationForm.filesFormArray.enable();
            this.showSaveButton = true;
        }

        // enable total need for system and region admins when application is not denied
        if (this.assistanceApplication?.status !== AssistanceApplicationStatus.Denied &&
            this.getApplicationTypeEnumValue(this.assistanceApplication.applicationType) === AssistanceApplicationType.STLWater &&
            (this.user?.userType === UserType.SystemAdmin || this.user?.userType === UserType.RegionAdmin)) {
            this.assistanceApplicationForm.form.get('totalNeed').enable();
            (this.assistanceApplicationForm.form.get('companies') as FormArray).controls.forEach(x => {
                x.get('amount').enable();
            });

            this.showSaveButton = true;
        }

        if (this.assistanceApplication.stormApplication) {
            const stormApp = this.assistanceApplication.stormApplication;

            this.assistanceApplicationForm.form.patchValue({
                stormApplication: {
                    id: stormApp.id,
                    assistanceApplicationId: stormApp.assistanceApplicationId,
                    damagedDwellingAddress1: stormApp?.damagedDwellingAddress1,
                    damagedDwellingAddress2: stormApp?.damagedDwellingAddress2,
                    damagedDwellingCity: stormApp?.damagedDwellingCity,
                    damagedDwellingState: stormApp?.damagedDwellingState,
                    damagedDwellingZipCode: stormApp?.damagedDwellingZipCode,
                    tierLevel: stormApp.tierLevel
                }
            });

            this.assistanceApplicationForm.form.get('hasDifferentDamagedDwellingAddress').setValue(!this.damagedEqualsClient());
        }
    }

    populateHouseHoldInformation(): void {
        const minorArray = this.assistanceApplicationForm.form.get('minors') as FormArray;
        const adultArray = this.assistanceApplicationForm.form.get('adults') as FormArray;
        const isOptional = this.assistanceApplication?.fundType?.isBirthYearAndSsnOptional ?? false;

        minorArray.clear();
        adultArray.clear();

        const minorBirthYearValidators = Validators.birthYear({required: !isOptional, kind: 'minor'});
        const minorSsnValidators = isOptional ? [] : [Validators.required];
        const adultBirthYearValidators = Validators.birthYear({required: !isOptional, kind: 'any'});
        const adultSsnValidators = isOptional ? [] : [Validators.required];

        this.assistanceApplication?.minors?.forEach(x => {
            const ssn = (x.last4Ssn) ? '***-**-' + x.last4Ssn : null;
            const fbGroup = this.formBuilder.group({
                id: x.id,
                firstName: [x.firstName, Validators.required],
                lastName: [x.lastName, Validators.required],
                birthYear: [x.birthYear, minorBirthYearValidators],
                ssn: [ssn, Validators.compose(minorSsnValidators)]
            });
            minorArray.push(fbGroup);
        });

        this.assistanceApplication?.adults?.forEach(x => {
            const ssn = (x.last4Ssn) ? '***-**-' + x.last4Ssn : null;
            const fbGroup = this.formBuilder.group({
                id: x.id,
                firstName: [x.firstName, Validators.required],
                lastName: [x.lastName, Validators.required],
                birthYear: [x.birthYear, adultBirthYearValidators],
                ssn: [ssn, Validators.compose(adultSsnValidators)],
                employmentStatus: [x.employmentStatus, Validators.required],
                monthlyIncomeAmount: [x.monthlyIncomeAmount, Validators.required]
            });
            adultArray.push(fbGroup);
        });

        this.formValuesSet = true;
        this.assistanceApplicationForm?.validateHouseHoldSize();
    }

    populateFiles(): void {
        const filesArray = this.assistanceApplicationForm.form.get('files') as FormArray;

        filesArray.clear();

        this.assistanceApplication?.files?.forEach(x => {
            const fbGroup = this.formBuilder.group({
                id: x.id,
                fileType: x.fileType,
                file: {name: x.fileName, fileUrl: x.fileUrl}
            });
            filesArray.push(fbGroup);
        });
    }

    async submit(): Promise<void> {
        if (!this.canEditBasedOnAudit && this.canUploadDocuments) {
            await this.assistanceApplicationForm.saveDocuments(this.assistanceApplication);
        } else {
            await this.assistanceApplicationForm.updateCase(this.assistanceApplication);
        }
    }

    printCase(): void {
        window.print();
    }

    getAuditStateLabel(): string {
        switch (this.auditState) {
            case AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Pass]:
                return 'Passed Audit';
            case AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Fail]:
                return 'Failed Audit';
            case AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.PassWithCorrection]:
                return 'Passed Audit with Correction';
            case AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.NotYetAudited]:
                return 'Not Yet Audited';
            case AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.PendingReAudit]:
                return 'Pending Re-Audit';
            default:
                return '';
        }
    }

    showAuditStateModal(): void {
        this.auditModal.open();
    }

    onClickCancelAuditState(): void {
        this.auditModal.close();
    }

    public async setReadyForReview(): Promise<void> {
        const result = await swal.fire({
            heightAuto: false,
            icon: 'question',
            showConfirmButton: true,
            confirmButtonText: 'Submit for Review',
            confirmButtonColor: '#006699',
            showDenyButton: true,
            denyButtonText: 'Cancel',
            title: 'Are you sure?',
            text: 'When submitting for review, the current application state will be saved. Then it will become read-only and be ready for audit by an authorized user.',
        });
        if (!result.isConfirmed) {
            return;
        }

        const success = await this.assistanceApplicationForm
            .updateCase(this.assistanceApplication, true, true);

        if (!success) {
            await swal.fire({
                heightAuto: false,
                icon: 'warning',
                showConfirmButton: true,
                confirmButtonText: 'Ok',
                confirmButtonColor: '#006699',
                showDenyButton: false,
                title: 'Oops',
                text: 'There seems to be one or more validation issues with the application form. Please correct any issues and try again. For specific details, look at the validation error section at the bottom of this page.',
            });
            return;
        }

        this._infoRequestClient.completeRequest(this.assistanceApplication?.currentInformationRequest?.id).subscribe({
            next: _ => {
                this._notificationService.showSuccess('Application status updated to Under Review.');
                window.location.reload();
            },
            error: err => {
                console.error(err);
                this._notificationService.showError('An error occurred while trying to update the application status. Please try again, or contact support if the issue persists.');
            }
        });
    }

    public async resubmit(): Promise<void> {
        const result = await swal.fire({
            heightAuto: false,
            icon: 'question',
            showConfirmButton: true,
            confirmButtonText: 'Resubmit',
            confirmButtonColor: '#006699',
            showDenyButton: true,
            denyButtonText: 'Cancel',
            title: 'Resubmit application?',
            text: 'The application will be moved back to Under Review and marked Pending Re-Audit. Any prior audit history is preserved.',
        });
        if (!result.isConfirmed) {
            return;
        }

        const success = await this.assistanceApplicationForm
            .updateCase(this.assistanceApplication, true, true);

        if (!success) {
            await swal.fire({
                heightAuto: false,
                icon: 'warning',
                showConfirmButton: true,
                confirmButtonText: 'Ok',
                confirmButtonColor: '#006699',
                showDenyButton: false,
                title: 'Oops',
                text: 'There seems to be one or more validation issues with the application form. Please correct any issues and try again. For specific details, look at the validation error section at the bottom of this page.',
            });
            return;
        }

        this.assistanceApplicationClient
            .resubmit(this.applicationId, new ResubmitAssistanceApplicationRequest())
            .subscribe({
                next: () => {
                    this._notificationService.showSuccess('Application resubmitted.');
                    window.location.reload();
                },
                error: err => {
                    console.error(err);
                    this._notificationService.showError('An error occurred while resubmitting the application. Please try again, or contact support if the issue persists.');
                }
            });
    }

    canAudit(): boolean {

        if (!this.assistanceApplication?.fundType) {
            return false;
        }

        const auditType = this.assistanceApplication.fundType.auditType;
        const isReviewAndAudit = auditType.toString() === AssistanceApplicationAuditType.ReviewAndAudit;
        const isAgencyReview = auditType.toString() === AssistanceApplicationAuditType.AgencyReview;

        if (isReviewAndAudit || isAgencyReview) {
            return this.user?.canAuditApplications;
        }

        if (this.user?.userType === UserType.SystemAdmin) {
            return true;
        }

        if (this.user?.agency?.isDefaultManagementAgency === true) {
            return true;
        }

        if (this.assistanceApplication?.isPublic === true) {
            return this.user?.userType === UserType.AgencyAdmin ||
                this.user?.userType === UserType.RegionAdmin ||
                this.user?.userType === UserType.User;
        }

        if (this.assistanceApplication?.isPublic === false) {
            return this.user?.userType === UserType.AgencyAdmin ||
                this.user?.userType === UserType.RegionAdmin ||
                this.user?.userType === UserType.User;
        }

        return false;
    }

    canEdit(): boolean {
        if (!this.canEditBasedOnAudit) {
            return false;
        }
        const isPendingReAudit = this.auditState === AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.PendingReAudit];
        return (this.user?.userType === UserType.SystemAdmin || this.user?.userType === UserType.RegionAdmin || this.user?.userType === UserType.AgencyAdmin) ||
            (this.isApplicationCreator && (this.isUnderReview || isPendingReAudit) && this.applicationPledgeStatusAllowsEdit);
    }

    shouldShowRegularAuditSection(): boolean {
        const auditType = this.assistanceApplication?.fundType?.auditType;
        return auditType.toString() === AssistanceApplicationAuditType.ReviewAndAudit;
    }

    shouldShowAgencyAuditSection(): boolean {
        const auditType = this.assistanceApplication.fundType.auditType;
        return auditType.toString() === AssistanceApplicationAuditType.AgencyReview;
    }

    ngAfterContentChecked(): void {
        this.cdref.detectChanges();
    }

    onUpdate(event: any): void {
        this.assistanceApplication = event;
        this.populateFiles();
    }

    private normalize(val?: string | null): string {
        return (val ?? '').trim().toUpperCase();
    }

    private damagedEqualsClient(): boolean {
        const application = this.assistanceApplication;

        const d1   = this.normalize(application.stormApplication.damagedDwellingAddress1);
        const d2   = this.normalize(application.stormApplication.damagedDwellingAddress2);
        const dCty = this.normalize(application.stormApplication.damagedDwellingCity);
        const dSt  = this.normalize(application.stormApplication.damagedDwellingState);
        const dZip = this.normalize(application.stormApplication.damagedDwellingZipCode);

        const c1   = this.normalize(this.client?.address1);
        const c2   = this.normalize(this.client?.address2);
        const cCty = this.normalize(this.client?.city);
        const cSt  = this.normalize(this.client?.state);
        const cZip = this.normalize(this.client?.zipCode);

        return d1 === c1 && d2 === c2 && dCty === cCty && dSt === cSt && dZip === cZip;
    }
}
